import type { Action } from "../actions/types";

export type CreditMemoStatus =
  | "Pending"
  | "AwaitingBankConfirmation"
  | "AwaitingPayout"
  | "Completed"
  | "Cancelled"
  | "Voided";

export interface CreditMemo {
  guid: string;
  businessGuid: string;
  businessName: string;
  status: CreditMemoStatus;
  hsaAmount: number;
  wsaAmount: number;
  contributionAmount: number;
  premiumTax: number;
  retailSalesTax: number;
  taxAmount: number;
  taxSource?: "AyaMaster" | "TaxBucket";
  totalAmount: number;
  invoiceNumber: string | null;
  approvedAt: string | null;
  taxTransferDoneAt: string | null;
  bankAccountMasked: string | null;
  bankConfirmationNote: string | null;
  bankConfirmedAt: string | null;
  moneySentAt: string | null;
  moneySentReference: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
  voidedAt: string | null;
  voidReason: string | null;
  insertedAt: string;
  lineCount?: number;
  // Where an approved memo's money waits: Return (and the tax bucket) since
  // approval stopped moving it, Available for memos approved before.
  moneyHeldIn?: "Available" | "Return";
  // The payout sent from this screen through DC Bank.
  payoutStatus?: CreditMemoPayoutStatus | null;
  payoutReferenceNumber?: number | null;
  payoutDestinationAccountName?: string | null;
  payoutSendingAt?: string | null;
  payoutFailureReason?: string | null;
}

export type CreditMemoPayoutStatus =
  | "Sending"
  | "Sent"
  | "NeedsCheck"
  | "Failed";

// The business wallet on Aya, with what approved memos not paid out yet keep
// in Return and in the tax bucket.
export interface CreditMemoWallet {
  available: number;
  hold: number;
  return: number;
  tax: number;
  heldReturn: number;
  heldTax: number;
}

export interface CreditMemoLine {
  guid: string;
  transactionGuid: string;
  personGuid: string;
  firstName: string;
  lastName: string;
  className: string;
  province: string;
  productName: string;
  transactionCode: string;
  transactionDate: string;
  amount: number;
  premiumTax: number;
  retailSalesTax: number;
  description: string;
}

export interface CreditMemoBalanceRow {
  scope: "wallet" | "member";
  entityGuid: string;
  entityName: string;
  ayaBalance: number;
  ayaGrace: number;
  authorizedClaims: number;
  authorizedHeldBack: number;
  pendingTaxTransfer: number;
  expectedDcBank: number;
  dcBankBalance: number | null;
  dcBankBalanceDate: string | null;
  difference?: number;
}

export interface CreditMemoBalanceCheck {
  balanced: boolean;
  membersChecked: number;
  wallet: CreditMemoBalanceRow | null;
  walletMismatch: CreditMemoBalanceRow | null;
  memberMismatches: CreditMemoBalanceRow[];
}

export interface CreditMemoDetail extends CreditMemo {
  lines: CreditMemoLine[];
  bankAccountOnFile: string;
  balanceCheck?: CreditMemoBalanceCheck;
  wallet?: CreditMemoWallet | null;
}

// Why the service did nothing. Shown in a pop-up.
export interface CreditMemoRefusal {
  reason: string;
  message: string;
  balanceCheck?: CreditMemoBalanceCheck;
  required?: number;
  available?: number;
}

export interface CreditMemoState {
  memos: CreditMemo[];
  memosLoading: boolean;
  memosError: string;

  selected: CreditMemoDetail | null;
  selectedLoading: boolean;
  selectedError: string;

  actionRunning: boolean;
  actionDone: string;
  refusal: CreditMemoRefusal | null;

  // The export being prepared, "pdf" or "xlsx", or "" when none is.
  exportRunning: string;
  exportError: string;
}

const initialState: CreditMemoState = {
  memos: [],
  memosLoading: false,
  memosError: "",

  selected: null,
  selectedLoading: false,
  selectedError: "",

  actionRunning: false,
  actionDone: "",
  refusal: null,

  exportRunning: "",
  exportError: "",
};

const creditMemoReducer = (
  state: CreditMemoState = initialState,
  action: Action
): CreditMemoState => {
  switch (action.type) {
    case "FETCH_CREDIT_MEMOS":
      return { ...state, memosLoading: true, memosError: "" };
    case "FETCH_CREDIT_MEMO":
      return {
        ...state,
        selectedLoading: true,
        selectedError: "",
        actionDone: "",
        exportError: "",
        // Keep the open memo on screen while its balances are being checked.
        selected:
          state.selected?.guid === action.data.creditMemoGuid
            ? state.selected
            : null,
      };
    case "RUN_CREDIT_MEMO_ACTION":
      return { ...state, actionRunning: true, actionDone: "", refusal: null };
    case "EXPORT_CREDIT_MEMO":
      return { ...state, exportRunning: action.data.format, exportError: "" };
    case "SET_CREDIT_MEMOS":
      return { ...state, ...action.data };
    case "CLEAR_CREDIT_MEMO_REFUSAL":
      return { ...state, refusal: null };
    default:
      return state;
  }
};

export default creditMemoReducer;
